import { useEffect, useRef, useState } from "react";
import ProductBadge from "./ProductBadge";

// The animated card art is ~0.5-0.9 MB per file, so a card first shows its
// light "-poster" still (frame 1 of the animation) and only downloads the
// animation once it is about to scroll into view. The prerender keeps the
// poster, so saved pages never reference the heavy files up front.
function useAnimatedArt(src) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!src || !el || window.__PRERENDER__ || !("IntersectionObserver" in window)) return undefined;
    let cancelled = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const full = new Image();
        full.onload = () => !cancelled && setReady(true);
        full.src = src;
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [src]);

  return [ref, src && (ready ? src : src.replace(/\.webp$/,"-poster.webp"))];
}

// Frameless round feature artwork: floats on the page with a soft accent
// glow and a floor shadow instead of sitting in a boxed card. Lifts on hover
// when a parent has the `group` class.
// `cat` is a FEATURE_CATEGORIES entry; pass `accent` alone for a product
// that has no feature page (it gets the same medallion shape with its icon).
export default function FeatureArt({ cat, accent = cat?.accent, alt = "", className = "" }) {
  const src = cat?.cardImage || cat?.image;
  const [artRef, artSrc] = useAnimatedArt(cat?.cardImage);
  return (
    <div className={`relative aspect-square w-full ${className}`}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-[16%] rounded-full opacity-25 blur-2xl transition-opacity duration-300 group-hover:opacity-50"
        style={{ background: accent.color }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-[3%] left-1/2 h-[5%] w-1/2 -translate-x-1/2 rounded-[100%] bg-[#0B1533]/20 blur-md transition-[transform,opacity] duration-300 ease-out group-hover:scale-x-[0.85] group-hover:opacity-60 motion-reduce:transform-none"
      />
      <div className="relative h-full w-full transition-transform duration-300 ease-out group-hover:-translate-y-2 group-hover:scale-[1.04] motion-reduce:transform-none">
        {cat?.cardImage ? (
          <img
            ref={artRef}
            src={artSrc}
            alt={alt}
            width="600"
            height="600"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-contain"
          />
        ) : (
          <div
            className="absolute inset-[9%] grid place-items-center overflow-hidden rounded-full border-4 border-white shadow-[0_10px_30px_rgba(11,21,51,0.10)]"
            style={{ background: `linear-gradient(145deg, #FFFFFF, ${accent.tint || "rgba(255,107,0,0.08)"})` }}
          >
            {src ? (
              <img src={src} alt={alt} loading="lazy" decoding="async" className="h-[78%] w-[78%] object-contain" />
            ) : (
              <ProductBadge p={accent} size="w-16 h-16" ic="w-8 h-8" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
